import i18n from '@/i18n'

const THOUSAND_SEPARATOR = "'"
const THOUSAND_SEPARATOR_REGEX = /\B(?=(?:\d{3})+(?!\d))/g

/**
 * Internal helper to format a number with thousand separators
 */
const _formatWithThousandSeparator = (value: number, separator: string = THOUSAND_SEPARATOR): string => {
  return Math.round(value).toString().replace(THOUSAND_SEPARATOR_REGEX, separator)
}

/**
 * Internal helper to format a date with locale
 */
const _formatDateWithLocale = (date: Date | null): string => {
  const locale = i18n.global.locale.value
  if (!date || !locale) {
    return ''
  }
  const formatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  return formatter.format(date)
}

/**
 * Formats a given number as a decimal with thousands separators.
 */
export const formatNumber = (value: number | null, separator: string = '.'): string | null => {
  return value ? _formatWithThousandSeparator(value, separator) : null
}

/**
 * Formats a timestamp to a locale date string
 */
export const formatTimestamp = (value: number | undefined): string => {
  const date = value ? new Date(value) : null
  return _formatDateWithLocale(date)
}

/**
 * Formats a timestamp to a locale date string with time (HH:MM)
 */
export const formatTimestampWithTime = (value: number | undefined): string => {
  const locale = i18n.global.locale.value
  if (!value || !locale) return ''
  const date = new Date(value)
  const formatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  return formatter.format(date)
}

/**
 * Formats a date string to a locale date string
 */
export const formatDate = (value: string): string => {
  const date = new Date(value)
  return _formatDateWithLocale(date)
}

/**
 * Formats a number with thousands separated 1'000
 */
export const formatNumberWithThousandSeparators = (value: number): string => {
  return _formatWithThousandSeparator(value)
}

/**
 * Formats a number to CHF with cents (e.g., 1'234.56)
 */
export const formatCHFWithCents = (chf: number): string => {
  return (Math.round(chf * 100) / 100).toString().replace(THOUSAND_SEPARATOR_REGEX, THOUSAND_SEPARATOR)
}

/**
 * Formats an area value with thousand separators
 */
export const formatArea = (m2: string | number): string => {
  return _formatWithThousandSeparator(Number(m2))
}

/**
 * Formats a number to look like CHF
 */
export const formatCHF = (chf: number): string => {
  return _formatWithThousandSeparator(chf)
}

/**
 * Formats hours with conditional precision
 */
export const formatHours = (h: number, _threshold: number): string => {
  void _threshold

  if (h === 0) {
    return '0'
  }

  const magnitude = Math.abs(h)

  if (magnitude >= 10) {
    return _formatWithThousandSeparator(h)
  }

  const fractionDigits = Math.max(1, Math.ceil(-Math.log10(magnitude)) + 1)
  return h.toFixed(fractionDigits)
}

/**
 * Formats a usage ratio as a percentage string (integer if whole, one decimal otherwise)
 */
export const formatUsageRatio = (ratio: number): string => {
  return Number.isInteger(ratio) ? String(ratio) : ratio.toFixed(1)
}

/**
 * Returns the currency code for a given country
 */
export const getCurrencyForCountry = (country: string): string => {
  const map: Record<string, string> = { CH: 'CHF', LU: 'EUR' }
  return map[country] ?? 'CHF'
}

/**
 * Formats an address from project general data
 */
export const formatAddress = (projectGeneral: Record<string, unknown>): string => {
  const street = projectGeneral?.houseNumber
    ? `${projectGeneral?.street} ${projectGeneral?.houseNumber}`
    : String(projectGeneral?.street || '')
  const city = projectGeneral?.zip
    ? `${projectGeneral?.zip} ${projectGeneral?.city}`
    : String(projectGeneral?.city || '')
  if (!street) {
    return city
  }
  return city ? `${street}, ${city}` : street
}
